(function attachCateringPackRules(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CateringPackRules = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createCateringPackRules() {
  'use strict';

  const DEFAULT_RULE = Object.freeze({
    pricingMode: 'per_person',
    minimumGuests: 4,
    defaultGuests: 10,
    guestIncrement: 2,
    meatPerPerson: 0.5,
    meatUnit: 'lb',
    meatMinimumPerOption: 1,
    meatAllocationStep: 1,
    meatMaximumSelections: 4,
    sidesPerPerson: 0.6,
    sideUnit: 'pt',
    sideRounding: 'nearest',
    sideMinimumPerOption: 1,
    sideAllocationStep: 1,
    sideMaximumSelections: 4,
    saucePerPerson: 0.2,
    sauceUnit: 'pt',
    sauceRounding: 'up',
    breadPerPerson: 1,
    breadUnit: 'slice',
    breadPackageSize: 10,
    breadPackageUnit: 'loaf',
  });

  function numberValue(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function integerValue(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : fallback;
  }

  function field(source, camelName, snakeName, fallback) {
    if (source && source[camelName] != null) return source[camelName];
    if (source && source[snakeName] != null) return source[snakeName];
    return fallback;
  }

  function normalizeRule(source) {
    const rule = source || {};
    return {
      pricingMode: String(field(rule, 'pricingMode', 'pricing_mode', DEFAULT_RULE.pricingMode)),
      minimumGuests: integerValue(field(rule, 'minimumGuests', 'minimum_guests', DEFAULT_RULE.minimumGuests), DEFAULT_RULE.minimumGuests),
      defaultGuests: integerValue(field(rule, 'defaultGuests', 'default_guests', DEFAULT_RULE.defaultGuests), DEFAULT_RULE.defaultGuests),
      guestIncrement: integerValue(field(rule, 'guestIncrement', 'guest_increment', DEFAULT_RULE.guestIncrement), DEFAULT_RULE.guestIncrement),
      meatPerPerson: numberValue(field(rule, 'meatPerPerson', 'meat_per_person', DEFAULT_RULE.meatPerPerson), DEFAULT_RULE.meatPerPerson),
      meatUnit: String(field(rule, 'meatUnit', 'meat_unit', DEFAULT_RULE.meatUnit)),
      meatMinimumPerOption: numberValue(field(rule, 'meatMinimumPerOption', 'meat_minimum_per_selected_option', DEFAULT_RULE.meatMinimumPerOption), DEFAULT_RULE.meatMinimumPerOption),
      // Meat portions are intentionally whole pounds, even if older metadata still stores half-pound steps.
      meatAllocationStep: 1,
      meatMaximumSelections: integerValue(field(rule, 'meatMaximumSelections', 'meat_maximum_selections', DEFAULT_RULE.meatMaximumSelections), DEFAULT_RULE.meatMaximumSelections),
      sidesPerPerson: numberValue(field(rule, 'sidesPerPerson', 'sides_per_person', DEFAULT_RULE.sidesPerPerson), DEFAULT_RULE.sidesPerPerson),
      sideUnit: String(field(rule, 'sideUnit', 'side_unit', DEFAULT_RULE.sideUnit)),
      sideRounding: String(field(rule, 'sideRounding', 'side_rounding', DEFAULT_RULE.sideRounding)),
      sideMinimumPerOption: numberValue(field(rule, 'sideMinimumPerOption', 'side_minimum_per_selected_option', DEFAULT_RULE.sideMinimumPerOption), DEFAULT_RULE.sideMinimumPerOption),
      sideAllocationStep: numberValue(field(rule, 'sideAllocationStep', 'side_allocation_step', DEFAULT_RULE.sideAllocationStep), DEFAULT_RULE.sideAllocationStep),
      sideMaximumSelections: integerValue(field(rule, 'sideMaximumSelections', 'side_maximum_selections', DEFAULT_RULE.sideMaximumSelections), DEFAULT_RULE.sideMaximumSelections),
      saucePerPerson: numberValue(field(rule, 'saucePerPerson', 'sauce_per_person', DEFAULT_RULE.saucePerPerson), DEFAULT_RULE.saucePerPerson),
      sauceUnit: String(field(rule, 'sauceUnit', 'sauce_unit', DEFAULT_RULE.sauceUnit)),
      sauceRounding: String(field(rule, 'sauceRounding', 'sauce_rounding', DEFAULT_RULE.sauceRounding)),
      breadPerPerson: numberValue(field(rule, 'breadPerPerson', 'bread_per_person', DEFAULT_RULE.breadPerPerson), DEFAULT_RULE.breadPerPerson),
      breadUnit: String(field(rule, 'breadUnit', 'bread_unit', DEFAULT_RULE.breadUnit)),
      breadPackageSize: integerValue(field(rule, 'breadPackageSize', 'bread_package_size', DEFAULT_RULE.breadPackageSize), DEFAULT_RULE.breadPackageSize),
      breadPackageUnit: String(field(rule, 'breadPackageUnit', 'bread_package_unit', DEFAULT_RULE.breadPackageUnit)),
    };
  }

  function roundValue(value, mode) {
    if (mode === 'up') return Math.ceil(value);
    if (mode === 'down') return Math.floor(value);
    return Math.round(value);
  }

  function roundDecimal(value, precision) {
    const factor = 10 ** (precision == null ? 4 : precision);
    return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
  }

  function isValidGuestCount(value, sourceRule) {
    const rule = normalizeRule(sourceRule);
    const guests = Number(value);
    return Number.isInteger(guests)
      && guests >= rule.minimumGuests
      && (guests - rule.minimumGuests) % rule.guestIncrement === 0;
  }

  function normalizeGuestCount(value, sourceRule) {
    const rule = normalizeRule(sourceRule);
    const requested = Number(value);
    if (!Number.isFinite(requested)) return rule.defaultGuests;
    const clamped = Math.max(rule.minimumGuests, requested);
    const steps = Math.round((clamped - rule.minimumGuests) / rule.guestIncrement);
    return rule.minimumGuests + (steps * rule.guestIncrement);
  }

  function calculatePackRequirements(value, sourceRule) {
    const rule = normalizeRule(sourceRule);
    const guests = normalizeGuestCount(value, rule);
    const meatQuantity = roundDecimal(guests * rule.meatPerPerson);
    const roundedSidePints = roundValue(guests * rule.sidesPerPerson, rule.sideRounding);
    const sideContainer = sideContainerForGuests(guests);
    const sideQuantity = Math.ceil(roundedSidePints / sideContainer.pints);
    const fulfilledSidePints = sideQuantity * sideContainer.pints;
    const sauceQuantity = roundValue(guests * rule.saucePerPerson, rule.sauceRounding);
    const breadSlices = Math.ceil(guests * rule.breadPerPerson);
    const breadPackages = Math.ceil(breadSlices / rule.breadPackageSize);

    return {
      guests,
      meat: {
        quantity: meatQuantity,
        unit: rule.meatUnit,
        minimumPerOption: rule.meatMinimumPerOption,
        step: rule.meatAllocationStep,
        maximumSelections: Math.min(rule.meatMaximumSelections, Math.floor(meatQuantity / rule.meatMinimumPerOption)),
      },
      sides: {
        quantity: sideQuantity,
        unit: sideContainer.unit,
        minimumPerOption: 1,
        step: 1,
        maximumSelections: Math.min(rule.sideMaximumSelections, sideQuantity),
        baseQuantityPints: fulfilledSidePints,
        roundedQuantityPints: roundedSidePints,
        containerSizePints: sideContainer.pints,
      },
      sauce: { quantity: sauceQuantity, unit: rule.sauceUnit },
      bread: {
        slices: breadSlices,
        sliceUnit: rule.breadUnit,
        packages: breadPackages,
        packageSize: rule.breadPackageSize,
        packageUnit: rule.breadPackageUnit,
      },
    };
  }

  function pluralize(value, singular, plural) {
    return Number(value) === 1 ? singular : (plural || `${singular}s`);
  }

  function formatNumber(value) {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Number(value));
  }

  function formatVolume(pints, options) {
    const quantity = Number(pints || 0);
    const long = Boolean(options && options.long);
    if (quantity >= 8) {
      const gallons = quantity / 8;
      const displayUnit = pluralize(gallons, long ? 'gallon' : 'gal', long ? 'gallons' : 'gal');
      const baseUnit = pluralize(quantity, long ? 'pint' : 'pt', long ? 'pints' : 'pt');
      return `${formatNumber(gallons)} ${displayUnit} (${formatNumber(quantity)} ${baseUnit})`;
    }
    if (quantity >= 2) {
      const quarts = quantity / 2;
      const displayUnit = pluralize(quarts, long ? 'quart' : 'qt', long ? 'quarts' : 'qt');
      const baseUnit = pluralize(quantity, long ? 'pint' : 'pt', long ? 'pints' : 'pt');
      return `${formatNumber(quarts)} ${displayUnit} (${formatNumber(quantity)} ${baseUnit})`;
    }
    return `${formatNumber(quantity)} ${pluralize(quantity, long ? 'pint' : 'pt', long ? 'pints' : 'pt')}`;
  }

  function sideContainerForGuests(guests) {
    const count = Number(guests || 0);
    if (count >= 40) return { unit: 'gal', pints: 8 };
    if (count >= 10) return { unit: 'qt', pints: 2 };
    return { unit: 'pt', pints: 1 };
  }

  function formatSideQuantity(sides, options) {
    const quantity = Number(sides?.quantity || 0);
    const long = Boolean(options && options.long);
    const unit = String(sides?.unit || 'pt');
    const labels = {
      pt: long ? ['pint', 'pints'] : ['pt', 'pt'],
      qt: long ? ['quart', 'quarts'] : ['qt', 'qt'],
      gal: long ? ['gallon', 'gallons'] : ['gal', 'gal'],
    };
    const [singular, plural] = labels[unit] || [unit, `${unit}s`];
    return `${formatNumber(quantity)} ${pluralize(quantity, singular, plural)}`;
  }

  function formatBread(bread) {
    const packagePlural = bread.packageUnit === 'loaf' ? 'loaves' : `${bread.packageUnit}s`;
    const packageUnit = pluralize(bread.packages, bread.packageUnit, packagePlural);
    const sliceUnit = pluralize(bread.slices, bread.sliceUnit, `${bread.sliceUnit}s`);
    return `${formatNumber(bread.packages)} ${packageUnit} / ${formatNumber(bread.slices)} ${sliceUnit}`;
  }

  function disposableWarePacks(guests, packageSize) {
    const count = Math.max(0, Number(guests || 0));
    const size = Math.max(1, Number(packageSize || 10));
    return Math.ceil(count / size);
  }

  function allocationContext(selected, total, step, minimum) {
    const names = Array.from(new Set((selected || []).filter(Boolean)));
    const normalizedStep = Number(step || 1);
    return {
      names,
      step: normalizedStep,
      totalUnits: Math.round(Number(total || 0) / normalizedStep),
      minimumUnits: Math.ceil(Number(minimum || 0) / normalizedStep),
    };
  }

  function balancedAllocations(selected, total, step, minimum) {
    const context = allocationContext(selected, total, step, minimum);
    if (!context.names.length || context.totalUnits <= 0) return {};
    if (context.minimumUnits * context.names.length > context.totalUnits) return {};
    const allocations = {};
    const remaining = context.totalUnits - (context.minimumUnits * context.names.length);
    const base = Math.floor(remaining / context.names.length);
    const extra = remaining % context.names.length;
    context.names.forEach((name, index) => {
      const units = context.minimumUnits + base + (index < extra ? 1 : 0);
      allocations[name] = roundDecimal(units * context.step);
    });
    return allocations;
  }

  function weightedUnits(names, totalUnits, minimumUnits, weights) {
    const assigned = Object.fromEntries(names.map((name) => [name, null]));
    let active = [...names];
    let remaining = totalUnits;
    while (active.length) {
      const activeWeight = active.reduce((sum, name) => sum + Number(weights[name] || 0), 0);
      const denominator = activeWeight > 0 ? activeWeight : active.length;
      const belowMinimum = active.filter((name) => {
        const weight = activeWeight > 0 ? Number(weights[name] || 0) : 1;
        return (remaining * weight) / denominator < minimumUnits;
      });
      if (!belowMinimum.length) break;
      belowMinimum.forEach((name) => {
        assigned[name] = minimumUnits;
        remaining -= minimumUnits;
      });
      active = active.filter((name) => !belowMinimum.includes(name));
    }
    if (active.length) {
      const activeWeight = active.reduce((sum, name) => sum + Number(weights[name] || 0), 0);
      const denominator = activeWeight > 0 ? activeWeight : active.length;
      const fractions = active.map((name, index) => {
        const weight = activeWeight > 0 ? Number(weights[name] || 0) : 1;
        const exact = (remaining * weight) / denominator;
        const whole = Math.floor(exact);
        assigned[name] = whole;
        return { name, index, fraction: exact - whole };
      });
      const allocated = active.reduce((sum, name) => sum + Number(assigned[name] || 0), 0);
      const remainder = remaining - allocated;
      fractions.sort((left, right) => right.fraction - left.fraction || left.index - right.index);
      for (let index = 0; index < remainder; index += 1) {
        assigned[fractions[index % fractions.length].name] += 1;
      }
    }
    return assigned;
  }

  function resizeAllocations(selected, total, step, minimum, previousAllocations) {
    const context = allocationContext(selected, total, step, minimum);
    if (!context.names.length || context.totalUnits <= 0) return {};
    if (context.minimumUnits * context.names.length > context.totalUnits) return {};
    const previous = previousAllocations || {};
    const weights = Object.fromEntries(context.names.map((name) => [name, Math.max(0, Number(previous[name] || 0))]));
    const assignedUnits = weightedUnits(context.names, context.totalUnits, context.minimumUnits, weights);
    return Object.fromEntries(context.names.map((name) => [name, roundDecimal(assignedUnits[name] * context.step)]));
  }

  function validateAllocations(selected, allocations, total, step, minimum, maximumSelections) {
    const context = allocationContext(selected, total, step, minimum);
    const values = allocations || {};
    const errors = [];
    if (!context.names.length) errors.push('Select at least one option.');
    if (maximumSelections && context.names.length > maximumSelections) {
      errors.push(`Select no more than ${maximumSelections} options.`);
    }
    context.names.forEach((name) => {
      const amount = Number(values[name]);
      if (!Number.isFinite(amount) || amount < Number(minimum || 0)) {
        errors.push(`${name} must be at least ${formatNumber(minimum)}.`);
      } else if (Math.abs((amount / context.step) - Math.round(amount / context.step)) > 0.0001) {
        errors.push(`${name} must use ${formatNumber(context.step)} increments.`);
      }
    });
    const assigned = context.names.reduce((sum, name) => sum + Number(values[name] || 0), 0);
    if (Math.abs(assigned - Number(total || 0)) > 0.0001) {
      errors.push(`Allocated total must equal ${formatNumber(total)}.`);
    }
    return { valid: errors.length === 0, errors, assigned: roundDecimal(assigned), total: Number(total || 0) };
  }

  function removeAllocation(selected, option, total, step, minimum, allocations) {
    const remaining = (selected || []).filter((name) => name !== option);
    return {
      selected: remaining,
      allocations: resizeAllocations(remaining, total, step, minimum, allocations),
    };
  }

  return Object.freeze({
    DEFAULT_RULE,
    normalizeRule,
    isValidGuestCount,
    normalizeGuestCount,
    calculatePackRequirements,
    formatVolume,
    formatSideQuantity,
    formatBread,
    disposableWarePacks,
    balancedAllocations,
    resizeAllocations,
    removeAllocation,
    validateAllocations,
  });
});
